/**
 * CommunityScreen — Sprint 2 Polish v2 router.
 *
 * Views:
 *   • home    — hero + Ask CTA + Browse Topics + 3 curated feed sections + Load More
 *   • topic   — dedicated topic page (search + sort + infinite scroll)
 *   • all     — All-Discussions page (same as topic, unscoped)
 *   • ask     — new-question flow (with pre-post Similar-Questions modal)
 *   • detail  — one question, with cached Understanding + experiences + related
 *
 * The router deliberately owns view state in-memory (no URLs yet) — this
 * matches the user's Sprint-2 decision (see PRD). A hash router will land
 * with shareable question links in a later sprint.
 */
import { useEffect } from 'react';
import { pageStyle } from './community/tokens';
import CommunityHome from './community/CommunityHome';
import DiscussionsListScreen from './community/DiscussionsListScreen';
import AskQuestionFlow from './community/AskQuestionFlow';
import QuestionDetailScreen from './community/QuestionDetailScreen';
import { useState, useRef } from 'react';

export default function CommunityScreen({ userId, onNavigate }) {
  const [view, setView] = useState({ name: 'home' });
  const mountedRef = useRef(false);
  useEffect(() => { mountedRef.current = true; }, []);

  /* Escape hatch used by App.js when the user re-taps the Community tab
   * while inside a nested view — pops back to the feed home. */
  useEffect(() => {
    window.__resetCommunity = () => setView({ name: 'home' });
    return () => { if (window.__resetCommunity) delete window.__resetCommunity; };
  }, []);

  const goHome   = () => setView({ name: 'home' });
  const goAsk    = () => setView({ name: 'ask' });
  const goDetail = (q) => setView({ name: 'detail', question: q });
  const goTopic  = (topic) => setView({ name: 'topic', topic });
  const goAll    = () => setView({ name: 'all' });

  return (
    <div data-testid="community-screen" style={pageStyle}>
      {view.name === 'home' && (
        <CommunityHome
          onAsk={goAsk}
          onOpenQuestion={goDetail}
          onOpenTopic={goTopic}
          onOpenAll={goAll}
        />
      )}
      {view.name === 'topic' && (
        <DiscussionsListScreen
          topic={view.topic}
          onBack={goHome}
          onOpenQuestion={goDetail}
        />
      )}
      {view.name === 'all' && (
        <DiscussionsListScreen
          topic={null}
          onBack={goHome}
          onOpenQuestion={goDetail}
        />
      )}
      {view.name === 'ask' && (
        <AskQuestionFlow
          userId={userId}
          onClose={goHome}
          onOpenExisting={goDetail}
        />
      )}
      {view.name === 'detail' && (
        <QuestionDetailScreen
          userId={userId}
          question={view.question}
          onBack={goHome}
          onOpenQuestion={goDetail}
          onNavigate={onNavigate}
        />
      )}
    </div>
  );
}
